import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i1 from '@engineering11/ui-lib/e11-ui-utils';
import { E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';

/**
 * A component that displays an avatar.
 * It can display an image, initials, or a color.
 * @example
 * <e11-avatar initials="AT" colorSeed="wtn3yKY7FDgobxxrMiOV" size="xl" shape="circular" hasBorder="false" hasRing="false" sizeOverride="md"></e11-avatar>
 * @example
 * <e11-avatar imageUrl="https://randomuser.me/api/portraits
 */
class E11AvatarComponent {
    /**
     * Emits events when the edit button is clicked.
     */
    onEditClick() {
        this.onEdit.emit();
        // TODO: Remove this in the next major release.
        this.edit.emit();
    }
    /**
     * Constructor for the component.
     * @param {ChangeDetectorRef} changeDetector - The change detector service.
     */
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        /**
         * The size of the image. Defaults to 'md'.
         * @type {ImageSize|undefined}
         * @default 'md'
         */
        this.size = 'md';
        /**
         * The shape of the image. Defaults to 'circular'.
         * @type {Shape}
         * @default 'circular'
         */
        this.shape = 'circular';
        /**
         * The title text of the image.
         * @type {string}
         * @default ''
         */
        this.imageTitleText = '';
        /**
         * The fit style of the image. Defaults to 'cover'.
         * @type {ImageFit|undefined}
         * @default 'cover'
         */
        this.imageFit = 'cover';
        /**
         * Indicates if the image is editable.
         * @type {boolean|undefined}
         * @default false
         */
        this.editable = false;
        /**
         * Indicates if the image should be regenerated.
         * @type {boolean|undefined}
         * @default false
         */
        this.regenerate = false;
        /**
         * Indicates if the image should have a border.
         * @type {boolean}
         * @default false
         */
        this.hasBorder = false;
        /**
         * Indicates if the image should have a ring.
         * @type {boolean}
         * @default false
         */
        this.hasRing = false;
        // TODO: Remove this in the next major release.
        /**
         * Event emitted when the image is edited.
         * @deprecated This property is deprecated. Please use {@link onEdit} instead.
         * @type {EventEmitter<void>}
         */
        this.edit = new EventEmitter();
        /**
         * Event emitted when the image is edited.
         * @type {EventEmitter<void>}
         */
        this.onEdit = new EventEmitter();
        /**
         * Indicates if the image fallback is active.
         * @type {boolean}
         * @default false
         */
        this.imageFallBack = false;
        /**
         * The initials to display when the image is unavailable.
         * @type {string}
         * @default '...'
         */
        this.fallbackInitials = '...';
    }
    /**
     * Angular lifecycle hook that is called after the component's view has been fully initialized.
     */
    ngAfterViewInit() {
        if (this.imageUrl && !this.imageFallBack) {
            this.initials = undefined;
        }
        this.changeDetector.detectChanges();
    }
    /**
     * Handles the image error event.
     * @param {Event} event - The image error event.
     */
    onImageError(event) {
        if (event) {
            this.imageFallBack = true;
        }
        else {
            this.imageFallBack = false;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AvatarComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11AvatarComponent, isStandalone: true, selector: "e11-avatar", inputs: { colorSeed: "colorSeed", initials: "initials", size: "size", shape: "shape", sizeOverride: "sizeOverride", imageUrl: "imageUrl", imageTitleText: "imageTitleText", imageFit: "imageFit", editable: "editable", regenerate: "regenerate", hasBorder: "hasBorder", hasRing: "hasRing" }, outputs: { edit: "edit", onEdit: "onEdit" }, ngImport: i0, template: "<div\n  class=\"e11-avatar e11-avatar-size {{\n    size\n  }} e11-flex e11-items-center e11-relative e11-font-skin-primary e11-justify-center e11-text-white e11-font-medium e11-overflow-clip\"\n  [ngClass]=\"{\n    'e11-rounded-lg': shape === 'rounded',\n    'e11-rounded-full': shape === 'circular',\n    'e11-border e11-border-skin-black/70': hasBorder || hasRing,\n    'e11-p-[2px]': hasRing\n  }\"\n  [ngStyle]=\"{\n    height: sizeOverride,\n    width: sizeOverride,\n    'background-color': imageUrl ? 'white' : imageFallBack ? 'grey' : (colorSeed | stringToColor : '35%')\n  }\"\n>\n  <!-- Not editable on xs. The edit icon is larger than the image -->\n  <span\n    *ngIf=\"imageUrl && editable && size !== 'xs'\"\n    (click)=\"onEditClick()\"\n    class=\"material-icons hover:e11-cursor-pointer e11-absolute e11--bottom-1 e11--right-1 e11-text-skin-base hover:e11-text-skin-dark e11-border e11-border-skin-grey e11-bg-white e11-p-[2px] e11-rounded-full\"\n    [ngClass]=\"{\n      'md-14': size === 'sm' || size === 'md',\n      'md-18': size === 'lg' || size === 'xl'\n    }\"\n  >\n    <ng-container *ngIf=\"editable && !regenerate\">photo_camera</ng-container>\n    <ng-container *ngIf=\"regenerate\">refresh</ng-container>\n  </span>\n\n  <img\n    *ngIf=\"imageUrl && !imageFallBack\"\n    class=\"\"\n    [ngClass]=\"{\n      'e11-rounded-lg': shape === 'rounded',\n      'e11-rounded-full': shape === 'circular',\n      'e11-object-contain': imageFit === 'contain',\n      'e11-object-cover': imageFit === 'cover'\n    }\"\n    [src]=\"imageUrl\"\n    title=\"{{ imageTitleText }}\"\n    (error)=\"onImageError($event)\"\n  />\n  <ng-container *ngIf=\"initials && (imageFallBack || !imageUrl)\">\n    {{ initials.substring(0, 2) }}\n  </ng-container>\n  <span\n    class=\"material-icons\"\n    *ngIf=\"!initials && (imageFallBack || !imageUrl)\"\n    [ngClass]=\"{\n      'md-14': size === 'xs' || size === 'sm',\n      'e11-text-3xl': size === 'lg',\n      'e11-text-4xl': size === 'xl',\n      'e11-text-6xl': size === 'xxl',\n      'e11-text-8xl': size === 'xxxl',\n      'e11-text-9xl': size === 'xxxxl'\n    }\"\n    >person</span\n  >\n</div>\n", styles: [".e11-avatar{height:42px;width:42px;text-shadow:1px 1px 0px grey;background-clip:content-box;clip-path:circle(150px at center)}.e11-avatar img{min-height:100%;min-width:100%}.e11-avatar.e11-avatar-size.xs{height:18px;width:18px;font-size:.5rem}.e11-avatar.e11-avatar-size.sm{height:32px;width:32px;font-size:.8625rem}.e11-avatar.e11-avatar-size.md{height:42px;width:42px;font-size:1.15rem}.e11-avatar.e11-avatar-size.lg{height:52px;width:52px;font-size:1.35rem}.e11-avatar.e11-avatar-size.xl{height:62px;width:62px;font-size:2rem}.e11-avatar.e11-avatar-size.xxl{height:84px;width:84px;font-size:2.5rem}.e11-avatar.e11-avatar-size.xxxl{height:112px;width:112px;font-size:3rem}.e11-avatar.e11-avatar-size.xxxxl{height:140px;width:140px;font-size:4rem}\n"], dependencies: [{ kind: "ngmodule", type: E11UtilModule }, { kind: "pipe", type: i1.E11StringToColorPipe, name: "stringToColor" }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AvatarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-avatar', standalone: true, imports: [E11UtilModule, CommonModule], encapsulation: ViewEncapsulation.None, template: "<div\n  class=\"e11-avatar e11-avatar-size {{\n    size\n  }} e11-flex e11-items-center e11-relative e11-font-skin-primary e11-justify-center e11-text-white e11-font-medium e11-overflow-clip\"\n  [ngClass]=\"{\n    'e11-rounded-lg': shape === 'rounded',\n    'e11-rounded-full': shape === 'circular',\n    'e11-border e11-border-skin-black/70': hasBorder || hasRing,\n    'e11-p-[2px]': hasRing\n  }\"\n  [ngStyle]=\"{\n    height: sizeOverride,\n    width: sizeOverride,\n    'background-color': imageUrl ? 'white' : imageFallBack ? 'grey' : (colorSeed | stringToColor : '35%')\n  }\"\n>\n  <!-- Not editable on xs. The edit icon is larger than the image -->\n  <span\n    *ngIf=\"imageUrl && editable && size !== 'xs'\"\n    (click)=\"onEditClick()\"\n    class=\"material-icons hover:e11-cursor-pointer e11-absolute e11--bottom-1 e11--right-1 e11-text-skin-base hover:e11-text-skin-dark e11-border e11-border-skin-grey e11-bg-white e11-p-[2px] e11-rounded-full\"\n    [ngClass]=\"{\n      'md-14': size === 'sm' || size === 'md',\n      'md-18': size === 'lg' || size === 'xl'\n    }\"\n  >\n    <ng-container *ngIf=\"editable && !regenerate\">photo_camera</ng-container>\n    <ng-container *ngIf=\"regenerate\">refresh</ng-container>\n  </span>\n\n  <img\n    *ngIf=\"imageUrl && !imageFallBack\"\n    class=\"\"\n    [ngClass]=\"{\n      'e11-rounded-lg': shape === 'rounded',\n      'e11-rounded-full': shape === 'circular',\n      'e11-object-contain': imageFit === 'contain',\n      'e11-object-cover': imageFit === 'cover'\n    }\"\n    [src]=\"imageUrl\"\n    title=\"{{ imageTitleText }}\"\n    (error)=\"onImageError($event)\"\n  />\n  <ng-container *ngIf=\"initials && (imageFallBack || !imageUrl)\">\n    {{ initials.substring(0, 2) }}\n  </ng-container>\n  <span\n    class=\"material-icons\"\n    *ngIf=\"!initials && (imageFallBack || !imageUrl)\"\n    [ngClass]=\"{\n      'md-14': size === 'xs' || size === 'sm',\n      'e11-text-3xl': size === 'lg',\n      'e11-text-4xl': size === 'xl',\n      'e11-text-6xl': size === 'xxl',\n      'e11-text-8xl': size === 'xxxl',\n      'e11-text-9xl': size === 'xxxxl'\n    }\"\n    >person</span\n  >\n</div>\n", styles: [".e11-avatar{height:42px;width:42px;text-shadow:1px 1px 0px grey;background-clip:content-box;clip-path:circle(150px at center)}.e11-avatar img{min-height:100%;min-width:100%}.e11-avatar.e11-avatar-size.xs{height:18px;width:18px;font-size:.5rem}.e11-avatar.e11-avatar-size.sm{height:32px;width:32px;font-size:.8625rem}.e11-avatar.e11-avatar-size.md{height:42px;width:42px;font-size:1.15rem}.e11-avatar.e11-avatar-size.lg{height:52px;width:52px;font-size:1.35rem}.e11-avatar.e11-avatar-size.xl{height:62px;width:62px;font-size:2rem}.e11-avatar.e11-avatar-size.xxl{height:84px;width:84px;font-size:2.5rem}.e11-avatar.e11-avatar-size.xxxl{height:112px;width:112px;font-size:3rem}.e11-avatar.e11-avatar-size.xxxxl{height:140px;width:140px;font-size:4rem}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { colorSeed: [{
                type: Input
            }], initials: [{
                type: Input
            }], size: [{
                type: Input
            }], shape: [{
                type: Input
            }], sizeOverride: [{
                type: Input
            }], imageUrl: [{
                type: Input
            }], imageTitleText: [{
                type: Input
            }], imageFit: [{
                type: Input
            }], editable: [{
                type: Input
            }], regenerate: [{
                type: Input
            }], hasBorder: [{
                type: Input
            }], hasRing: [{
                type: Input
            }], edit: [{
                type: Output
            }], onEdit: [{
                type: Output
            }] } });

class E11AvatarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AvatarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11AvatarModule, imports: [E11AvatarComponent], exports: [E11AvatarComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AvatarModule, imports: [E11AvatarComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11AvatarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11AvatarComponent],
                    exports: [E11AvatarComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11AvatarComponent, E11AvatarModule };
